// /coaching Service + FAQPage schema — single source of truth (SEO Play A).
//
// WHY THIS MODULE (mirrors app/setups/faqData.js): the VISIBLE coaching FAQ and
// the FAQPage JSON-LD MUST stay in lockstep — Google discounts (or flags)
// structured data whose Q&A text isn't present on the page. Keeping BOTH the
// rendered accordion and the JSON-LD from ONE plain-data array makes drift
// structurally impossible. Pure data + JSON-LD builders, no 'use client' and no
// server-only imports, so the client island (components/CoachingSeo) AND a unit
// test can both import it.
//
// GSC INTENT (already page 1, schema is the missing lever): "iracing coaching"
// ~1070 impr @ pos 2.8 · "iracing coach" 248 @ 3.9 · "sim racing coaching" 447 @
// 12.7. Live /coaching HTML carried ONLY Organization + WebSite + Breadcrumb —
// no Service, no FAQPage. This adds both.
//
// FACTUAL PROVENANCE (no invented facts): every claim below is grounded in the
// /coaching dataset copy — "80+" champion coaches; coaches "join you on track" and
// read "real telemetry"; "1-on-1", "bespoke private group sessions", "public group
// trainings" on "driving, race craft and setup building"; "three subscription
// plans" between sessions. The two Q&As that already lived in the page's prose
// (sdC4 "How does an iRacing coaching session work?" / sdC6 "What makes VRS
// coaching different?") are reproduced VERBATIM and moved into this FAQ set, so the
// same text still ships on the page — just surfaced as a real FAQ section. We do
// NOT invent per-coach prices (coaching is priced per coach): the Service carries
// NO Offer/priceRange, and the cost Q&A points to the coach's own profile.

// The site-wide Organization node's @id (emitted by app/layout.js via
// components/seo/SiteJsonLd.app.jsx, ORG_ID = `${SITE_URL}/#organization`). The
// Service references it by @id so the graph links to the existing Organization
// rather than re-declaring it. Kept in sync with SiteJsonLd (asserted by the unit
// test).
export const ORG_ID = 'https://vrs.racing/#organization'

// The catch-all page slug (utils/urls slugParamToPath) for the /coaching route.
// The App Router island emits this schema ONLY for this slug.
export const COACHING_ROUTE_SLUG = 'coaching'

/**
 * @typedef {Object} CoachingFaq
 * @property {string} question
 * @property {string} answer  plain-text (single paragraph) — rendered visibly AND
 *                            used verbatim as the JSON-LD acceptedAnswer.
 */

/** @type {CoachingFaq[]} */
export const COACHING_FAQS = [
  {
    // VERBATIM from the /coaching dataset prose (richText sdC4) — surfaced here.
    question: 'How does an iRacing coaching session work?',
    answer:
      'In a 1-on-1 session your VRS coach joins you on track, analyses your driving in real time and shows you exactly where and how to find time — turning ‘you versus the track’ into the two of you working the track together. Prefer to learn alongside others? Some coaches offer bespoke private group sessions for you and your team, and there are public group trainings focused on the skills that matter most: driving, race craft and setup building.',
  },
  {
    // VERBATIM from the /coaching dataset prose (richText sdC6) — surfaced here.
    question: 'What makes VRS coaching different?',
    answer:
      'In racing, lap times never lie — and nor does data. VRS pairs world-class coaches with real telemetry: alongside your sessions, three subscription plans let you analyse and level up your own performance between coaching. It is the same data-driven approach our coaches use to win, applied to your driving.',
  },
  {
    question: 'How much does iRacing coaching cost?',
    answer:
      'Coaching is booked per session and priced by each coach, so the cost depends on the coach you choose and whether you book a 1-on-1 or a group session. You will see a coach’s current rate on their VRS profile before you book. Between sessions, three VRS subscription plans let you keep analysing your own telemetry so every paid hour with a coach goes further.',
  },
  {
    question: 'How do I book a sim racing coaching session?',
    answer:
      'You book coaching through VRS: browse the roster of 80+ champion coaches, pick the coach whose experience fits your car and discipline, and request a 1-on-1 or group session with them directly. Your coach then joins you on track and works through your telemetry with you.',
  },
  {
    question: 'Do you offer 1-on-1 or group coaching?',
    answer:
      'Both. Most drivers start with 1-on-1 sessions where a coach works only with you, but many coaches also run bespoke private group sessions for you and your team, and VRS runs public group trainings focused on the skills that matter most: driving, race craft and setup building.',
  },
  {
    question: 'Does VRS coach ACC and Le Mans Ultimate too, or only iRacing?',
    answer:
      'VRS coaching is built around iRacing, where our 80+ coaches race and win at the highest level across road, dirt and oval. The fundamentals they teach — braking, race craft, setup and telemetry analysis — carry directly across to other sims, so if you race Assetto Corsa Competizione (ACC) or Le Mans Ultimate (LMU) tell your coach and they will tailor the session to the title you drive.',
  },
]

/**
 * FAQPage JSON-LD from COACHING_FAQS. Emitted as a raw
 * <script type="application/ld+json"> by the client island (App-Router-safe:
 * next-seo's <FAQPageJsonLd> throws under App Router SSR — see
 * components/sections/Products/ProductFaq and app/setups/faqData.js).
 * @param {CoachingFaq[]} [faqs]
 */
export function buildCoachingFaqJsonLd(faqs = COACHING_FAQS) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  }
}

// Static, provenance-grounded Service fields (see FACTUAL PROVENANCE above).
export const COACHING_SERVICE = Object.freeze({
  name: 'Sim Racing Coaching',
  serviceType: 'Sim racing coaching',
  areaServed: 'Worldwide',
  description:
    '1-on-1 and group sim racing coaching from a roster of 80+ champion coaches, including multiple iRacing world champions. Your coach joins you on track and reads your telemetry to show you exactly where and how to find lap time.',
})

/**
 * Service JSON-LD for /coaching. The provider is the existing site Organization
 * (referenced by @id, not re-declared). Coaching is priced per coach, so — per the
 * brief — we deliberately carry NO single fixed Offer and NO invented priceRange;
 * a genuine AggregateOffer can be added here later if a real range becomes
 * available. Emitted as a raw <script> by the client island, same App-Router-safe
 * pattern as SiteJsonLd / ProductJsonLd.
 *
 * @param {Object} [opts]
 * @param {string} [opts.url]  canonical page URL (adds Service.url when present).
 * @returns {Object} the schema.org Service object.
 */
export function buildCoachingServiceJsonLd({ url } = {}) {
  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: COACHING_SERVICE.name,
    serviceType: COACHING_SERVICE.serviceType,
    provider: { '@id': ORG_ID },
    areaServed: COACHING_SERVICE.areaServed,
    description: COACHING_SERVICE.description,
  }
  if (url) {
    service.url = url
  }
  return service
}
